/**
 * The place an operator types when the phone has no GPS fix (SC-018): an MGRS reference
 * ("34U DV 48746 34496", spaces optional) or decimal latitude and longitude, with a dot or a
 * Slovak/Czech decimal comma ("48,7164 21,2611"), signed or with N/S/E/W. Degrees-minutes-
 * seconds are not read.
 */
import { type MgrsDigits, parseMgrs, toMgrs } from './mgrs'

export interface TypedLocation {
  lat: number
  lon: number
  /** The MGRS readout of the position, at the precision that was typed (1 m for decimals). */
  mgrs: string
  kind: 'mgrs' | 'decimal'
}

/** Numbers, hemisphere letters, and what may sit between them (spaces, degree sign, , ;). */
const TOKEN = /[-+]?\d{1,3}(?:[.,]\d+)?|[NSEW]|[\s°,;]+|./g
const NUMBER = /^[-+]?\d/

interface Coordinate {
  value: number
  /** Named by its letter; `null` = by its place (latitude first). */
  axis: 'lat' | 'lon' | null
}

/** A number with its letter; a sign and a letter together are refused ("-48S"). */
function coordinate(number: string, letter?: string): Coordinate | null {
  const value = Number(number.replace(',', '.'))
  if (!letter) return { value, axis: null }
  if (value < 0) return null
  const negative = letter === 'S' || letter === 'W'
  return {
    value: negative ? -value : value,
    axis: letter === 'N' || letter === 'S' ? 'lat' : 'lon',
  }
}

/**
 * Pair the numbers with their letters: "48.7 21.2", "N48.7 E21.2" or "48.7N 21.2E". Mixed
 * or missing letters are refused rather than guessed.
 */
function coordinates(text: string): [Coordinate | null, Coordinate | null] | null {
  const parts: string[] = text.match(TOKEN) ?? []
  if (parts.some(part => part.length === 1 && !/[NSEW\s°,;\d]/.test(part))) return null
  const kept = parts.filter(part => !/^[\s°,;]+$/.test(part))
  const numbers = kept.filter(part => NUMBER.test(part))
  const letters = kept.filter(part => !NUMBER.test(part))
  if (numbers.length !== 2) return null
  if (letters.length === 0) return [coordinate(numbers[0]), coordinate(numbers[1])]
  if (letters.length !== 2) return null
  const prefix = !NUMBER.test(kept[0])
  const expected = prefix ? [false, true, false, true] : [true, false, true, false]
  if (kept.some((part, i) => NUMBER.test(part) !== expected[i])) return null
  return [coordinate(numbers[0], letters[0]), coordinate(numbers[1], letters[1])]
}

/** Two coordinates, latitude first unless the letters say otherwise. */
function assignAxes([first, second]: [Coordinate, Coordinate]): {
  lat: number
  lon: number
} | null {
  if (first.axis && first.axis === second.axis) return null
  const swapped = first.axis === 'lon' || second.axis === 'lat'
  return swapped ? { lat: second.value, lon: first.value } : { lat: first.value, lon: second.value }
}

function parseDecimal(text: string): { lat: number; lon: number } | null {
  const [first, second] = coordinates(text.toUpperCase().trim()) ?? []
  if (!first || !second) return null
  const position = assignAxes([first, second])
  if (!position) return null
  const { lat, lon } = position
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? position : null
}

/** Round to 6 decimals (11 cm): a typed position claims no more than that. */
const round6 = (value: number) => Math.round(value * 1e6) / 1e6

/**
 * The typed place as a position with its MGRS, or `null` when it is neither a valid MGRS
 * reference nor a latitude/longitude pair inside the grid's range (80° S to 84° N).
 */
export function parseTypedLocation(text: string): TypedLocation | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const grid = parseMgrs(trimmed)
  if (grid) {
    const mgrs = toMgrs(grid.lat, grid.lon, grid.digits as MgrsDigits)
    return mgrs && { lat: round6(grid.lat), lon: round6(grid.lon), mgrs, kind: 'mgrs' }
  }
  const decimal = parseDecimal(trimmed)
  if (!decimal) return null
  const mgrs = toMgrs(decimal.lat, decimal.lon)
  return mgrs && { lat: decimal.lat, lon: decimal.lon, mgrs, kind: 'decimal' }
}
